import React from 'react';
import { PlayIcon, PlusIcon, UsersIcon, XIcon } from 'lucide-react';
import { Button } from '../ui/Button';
import { Waveform } from './AudioTile';
import { StatusBadge } from '../ui/StatusBadge';
import { ARCHIVE_KIND_LABELS } from '../../data/archive';
import type { ArchiveItem } from '../../data/archive';
interface ArchiveDetailProps {
  item: ArchiveItem | null;
  onClose: () => void;
}
export function ArchiveDetail({
  item,
  onClose
}: ArchiveDetailProps) {
  if (!item) return null;
  return <div role="dialog" aria-modal="true" aria-label={item.title} className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-4 sm:p-8" data-id="element-10" data-mp="3u5xba5w832o0">
      {/* Scrim as its own element — opacity modifiers don't apply to the
          CSS-variable colour tokens, so `bg-ink/70` renders as nothing. */}
      <button type="button" aria-hidden="true" tabIndex={-1} onClick={onClose} className="fixed inset-0 cursor-default bg-ink opacity-80" data-id="element-11" data-mp="1ndehju3ptrnp" />

      <div className="relative w-full max-w-3xl overflow-hidden rounded-card border border-rule bg-card shadow-2xl" data-id="element-12" data-mp="9red91r59wkz">
        <div className="sticky top-0 z-10 flex items-start justify-between gap-4 border-b border-rule bg-card px-5 py-4" data-id="element-13" data-mp="epfwpnshfxwz">
          <div className="flex flex-col" data-id="element-14" data-mp="joj43evxqg84">
            <span className="text-eyebrow-caption font-semibold uppercase text-ink-muted" data-id="element-15" data-mp="opq8d3yvcxa1">
              {item.year} · {ARCHIVE_KIND_LABELS[item.kind]}
            </span>
            <h2 className="text-section-heading font-semibold text-ink" data-id="element-16" data-mp="3s5xf5jckzan1">{item.title}</h2>
          </div>
          <button type="button" onClick={onClose} className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-pill text-ink-muted hover:bg-canvas hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2" data-id="element-17" data-mp="105q6naji41t">
            <span className="sr-only" data-id="element-18" data-mp="39enuqf19v1go">Close</span>
            <XIcon aria-hidden="true" className="h-5 w-5" data-id="element-19" data-mp="31ezxp8b8umiv" />
          </button>
        </div>

        {item.kind === 'audio' ? <div className="flex flex-col gap-4 bg-ink px-5 py-6" data-id="element-20" data-mp="2wgonuxky8c0n">
            <div className="flex items-center gap-4 text-highlight" data-id="element-21" data-mp="2we2ecsehzmfs">
              <button type="button" className="flex h-14 w-14 shrink-0 items-center justify-center rounded-pill bg-highlight focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-on-ink focus-visible:ring-offset-2 focus-visible:ring-offset-[color:var(--color-ink)]" data-id="element-22" data-mp="3b49mofasjp2r">
                <span className="sr-only" data-id="element-23" data-mp="2djf3ne7suuic">Play, {item.duration}</span>
                <PlayIcon aria-hidden="true" className="h-6 w-6 translate-x-0.5 text-on-highlight" data-id="element-24" data-mp="2sfwapt8yhmz3" />
              </button>
              <Waveform seed={item.id} className="min-w-0 flex-1" data-id="element-25" data-mp="7n1bufehdphj" />
              <span className="shrink-0 text-small text-on-ink opacity-70" data-id="element-26" data-mp="2p9idgy6tvb8">
                {item.duration}
              </span>
            </div>
            {item.speaker ? <p className="text-small text-on-ink opacity-70" data-id="element-27" data-mp="aav9bm8ji2ct">{item.speaker}</p> : null}
          </div> : item.image ? <div className="bg-ink" data-id="element-28" data-mp="18m1530qul2l">
            <img src={item.image.src} alt={item.image.alt} className="max-h-[26rem] w-full object-contain" data-id="element-29" data-mp="19nb1460x1iq" />
          </div> : null}

        <div className="flex flex-col gap-5 px-5 py-5" data-id="element-30" data-mp="szjn7o8lv7f3">
          {item.transcript ? <div className="flex flex-col gap-2 rounded-field bg-canvas p-4" data-id="element-31" data-mp="xxl88epldgt7">
              <span className="text-eyebrow-caption font-semibold uppercase text-ink-muted" data-id="element-32" data-mp="k5n5qmqdyj60">
                From the transcript
              </span>
              <p className="text-body italic text-ink" data-id="element-33" data-mp="o5xaxx6ct5jt">{item.transcript}</p>
              <button type="button" className="inline-flex min-h-tap w-fit items-center rounded-control text-small font-medium text-primary-text underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2" data-id="element-34" data-mp="1bwb27lh470dc">
                Read the full transcript
              </button>
            </div> : null}

          {item.quote ? <p className="text-section-heading italic text-ink" data-id="element-35" data-mp="3b6e4j928o4sd">“{item.quote}”</p> : null}
          {item.note ? <p className="text-body text-ink-muted" data-id="element-36" data-mp="bppgpjwsfsfk">{item.note}</p> : null}

          {item.mine && item.status ? <div className="flex flex-wrap items-center gap-3 rounded-field bg-canvas px-4 py-3" data-id="element-37" data-mp="6s7iookb6xmc">
              <StatusBadge status={item.status} data-id="element-38" data-mp="1zedy0oubpa9" />
              <p className="text-small text-ink-muted" data-id="element-39" data-mp="3u502if2glfs2">
                {item.status === 'review' ? 'You added this. A staff member reads everything before it appears to other members, usually within a day.' : 'You added this, and it is visible to other members.'}
              </p>
            </div> : null}

          <dl className="grid gap-3 text-small sm:grid-cols-2" data-id="element-40" data-mp="28eurprzqel0f">
            <div className="flex flex-col gap-0.5 border-t border-rule pt-3" data-id="element-41" data-mp="2dbl6mbzsvak9">
              <dt className="text-ink-muted" data-id="element-42" data-mp="2he82cnzldfmq">Contributed</dt>
              <dd className="text-ink" data-id="element-43" data-mp="2hcn86zh6vype">{item.credit}</dd>
            </div>
            <div className="flex flex-col gap-0.5 border-t border-rule pt-3" data-id="element-44" data-mp="2mduf6wbictv0">
              <dt className="text-ink-muted" data-id="element-45" data-mp="2rcxlsl74z853">Year</dt>
              <dd className="text-ink" data-id="element-46" data-mp="2ra1masfn4bej">{item.year}</dd>
            </div>
            {item.duration ? <div className="flex flex-col gap-0.5 border-t border-rule pt-3" data-id="element-47" data-mp="2w8w4bs5s9vxl">
                <dt className="text-ink-muted" data-id="element-48" data-mp="312qvthrmrf78">Length</dt>
                <dd className="text-ink" data-id="element-49" data-mp="353slb6vqwcw0">{item.duration}</dd>
              </div> : null}
            <div className="flex flex-col gap-0.5 border-t border-rule pt-3" data-id="element-50" data-mp="3a3ezpjnb7z6e">
              <dt className="text-ink-muted" data-id="element-51" data-mp="3f1gtozm9xiwn">
                {item.kind === 'audio' ? 'Who is speaking' : 'Who is in it'}
              </dt>
              <dd className="text-ink" data-id="element-52" data-mp="1f08vxjs3c8x0">
                {item.unidentified ? `${item.unidentified} ${item.kind === 'audio' ? 'voices' : 'people'} unnamed` : item.speaker ?? 'Nobody named yet'}
              </dd>
            </div>
          </dl>

          {item.unidentified ? <div className="flex flex-col gap-3 rounded-field bg-primary-soft p-4" data-id="element-53" data-mp="1xpoxmafii7f0">
              <p className="text-body text-on-primary-soft" data-id="element-54" data-mp="1todgyuem8wiy">
                {item.unidentified} {item.kind === 'audio' ? 'voices' : 'people'} in this are
                unnamed. If you were here in {item.year}, you may be one of the few who can say
                who they are.
              </p>
              <div className="flex flex-wrap gap-2" data-id="element-55" data-mp="da3s7z6gp5rl">
                <Button data-id="element-56" data-mp="dawqscv9ooyn">
                  <UsersIcon aria-hidden="true" className="h-4 w-4" data-id="element-57" data-mp="dc7ukcoppwpr" />
                  Name someone
                </Button>
                <Button variant="secondary" data-id="element-58" data-mp="2jynml3wuycnv">
                  {item.kind === 'audio' ? 'I don’t recognize the voices' : 'I don’t recognize them'}
                </Button>
              </div>
            </div> : null}

          <div className="flex flex-wrap items-center gap-2 border-t border-rule pt-4" data-id="element-59" data-mp="2a8avesujfl8r">
            <Button variant="secondary" data-id="element-60" data-mp="2f62se1a1hxjt">
              <PlusIcon aria-hidden="true" className="h-4 w-4" data-id="element-61" data-mp="2185w75zhzt5o" />
              Add what you remember
            </Button>
            <span className="text-small text-ink-muted" data-id="element-62" data-mp="26792suv4m7fr">
              Anything you add is read by a staff member before it appears.
            </span>
          </div>
        </div>
      </div>
    </div>;
}